/**
 * Placeholder for the real Mapbox component shipping in Phase 9. Renders a
 * gridded "paper" background, faked road svg lines, and pin labels at
 * percent-coordinates. Same prop shape we plan to use for the real
 * Mapbox component so callers don't churn during the swap.
 *
 *   <MapStandin pins={[{ x: 35, y: 50, label: '12 lawyers', selected: true }]} />
 */

export interface MapPin {
    /** 0-100 horizontal percent within the container. */
    x: number;
    /** 0-100 vertical percent within the container. */
    y: number;
    label: string;
    selected?: boolean;
}

export interface MapStandinProps {
    height?: number;
    pins?: MapPin[];
    radius?: number;
    className?: string;
    /** Optional aria-label describing what the map is showing. */
    label?: string;
}

const GRID_STYLE: React.CSSProperties = {
    backgroundColor: '#ECEAE4',
    backgroundImage: `
        linear-gradient(rgba(11,15,20,.04) 1px, transparent 1px),
        linear-gradient(90deg, rgba(11,15,20,.04) 1px, transparent 1px),
        linear-gradient(rgba(11,15,20,.07) 1px, transparent 1px),
        linear-gradient(90deg, rgba(11,15,20,.07) 1px, transparent 1px)
    `,
    backgroundSize: '12px 12px, 12px 12px, 72px 72px, 72px 72px',
};

export function MapStandin({
    height = 400,
    pins = [],
    radius = 14,
    className,
    label = 'Map placeholder',
}: MapStandinProps) {
    const cls = [
        'relative overflow-hidden border border-rule',
        className ?? '',
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <div
            role="region"
            aria-label={label}
            className={cls}
            style={{ height, borderRadius: radius, ...GRID_STYLE }}
        >
            <svg
                width="100%"
                height="100%"
                className="absolute inset-0"
                aria-hidden="true"
            >
                <path
                    d="M-20 80 Q 200 60, 420 140 T 900 200"
                    stroke="rgba(11,15,20,.12)"
                    strokeWidth="6"
                    fill="none"
                />
                <path
                    d="M0 280 Q 240 240, 460 320 T 900 380"
                    stroke="rgba(11,15,20,.10)"
                    strokeWidth="4"
                    fill="none"
                />
                <path
                    d="M120 -20 L 200 600"
                    stroke="rgba(11,15,20,.10)"
                    strokeWidth="5"
                    fill="none"
                />
                <path
                    d="M540 -20 L 620 600"
                    stroke="rgba(11,15,20,.08)"
                    strokeWidth="4"
                    fill="none"
                />
                <path
                    d="M-20 450 L 900 470"
                    stroke="rgba(11,15,20,.10)"
                    strokeWidth="4"
                    fill="none"
                />
                <path
                    d="M-20 380 Q 100 360, 180 420 T 300 480 L 300 600 L -20 600 Z"
                    fill="#DEE3DC"
                    opacity="0.7"
                />
            </svg>

            {pins.map((pin) => (
                <div
                    key={`${pin.x}-${pin.y}-${pin.label}`}
                    className="absolute"
                    style={{
                        left: `${pin.x}%`,
                        top: `${pin.y}%`,
                        transform: 'translate(-50%, -100%)',
                    }}
                >
                    <div
                        className={[
                            'relative inline-flex items-center px-2.5 py-1 rounded-full',
                            'text-[11px] font-semibold whitespace-nowrap shadow-pop',
                            pin.selected === true
                                ? 'bg-ember text-paper border-2 border-card'
                                : 'bg-ink text-paper',
                        ].join(' ')}
                    >
                        {pin.label}
                        <span
                            aria-hidden="true"
                            className="absolute top-full left-1/2 -translate-x-1/2 size-0"
                            style={{
                                borderLeft: '5px solid transparent',
                                borderRight: '5px solid transparent',
                                borderTop: `6px solid ${pin.selected === true ? 'var(--color-ember)' : 'var(--color-ink)'}`,
                            }}
                        />
                    </div>
                </div>
            ))}

            <div className="absolute right-3 top-3 flex flex-col rounded-md overflow-hidden shadow-card bg-card">
                <button
                    type="button"
                    className="size-8 text-ink-soft hover:bg-paper-deep text-[16px] cursor-pointer"
                    aria-label="Zoom in"
                >
                    +
                </button>
                <div className="border-t border-rule" />
                <button
                    type="button"
                    className="size-8 text-ink-soft hover:bg-paper-deep text-[16px] cursor-pointer"
                    aria-label="Zoom out"
                >
                    −
                </button>
            </div>
        </div>
    );
}

export default MapStandin;
